import { AsyncPipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CountryService } from 'src/core/services/country.service';
import { AuthStore } from 'src/core/stores';
import {
  CoreAuthenticationService,
  Register201Response,
  RegisterUserRequestDto,
} from 'src/generated/api/nestjs-backend';
import { LoadingSpinnerComponent } from '../../shared/loading-spinner/loading-spinner.component';
import { passwordsMatchValidator } from '../validators/passwords-match.validator';

@Component({
  selector: 'core-register',
  templateUrl: './register.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    AsyncPipe,
    NgClass,
    LoadingSpinnerComponent,
  ],
})
export class RegisterComponent {
  private logger = inject(NGXLogger);
  private authStore = inject(AuthStore);
  private coreAuthenticationService = inject(CoreAuthenticationService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  countryService = inject(CountryService);

  private static readonly ALERT_DISPLAY_TIME = 5000;

  emailTakenError$$ = new BehaviorSubject(false);
  genericError$$ = new BehaviorSubject(false);
  userForm: FormGroup;
  titles: string[] = ['Mr.', 'Ms.', 'Mrs.', 'Dr.'];
  selectedTitle: string | null = null;
  redirectUrl: string =
    this.activatedRoute.snapshot.queryParams['redirect'] || '/';

  readonly isLoading = signal(true);
  readonly submitted = signal(false);
  readonly showAlert = signal(false);
  readonly enableDoubleOptIn = signal(true);
  readonly showPassword = signal(false);

  constructor() {
    effect(() => {
      if (this.authStore.isAuthenticated()) {
        void this.router.navigateByUrl(this.redirectUrl);
      }
    });
    this.userForm = this.initFormValidation();
    this.getRegistrationConfiguration();

    if (this.translocoService.getActiveLang() === 'it') {
      this.userFormCountry?.setValue('IT');
    } else {
      this.userFormCountry?.setValue('DE');
    }
  }

  get userFormTitle() {
    return this.userForm.get('userFormTitle');
  }

  get userFormFirstName() {
    return this.userForm.get('userFormFirstName');
  }

  get userFormLastName() {
    return this.userForm.get('userFormLastName');
  }

  get userFormEmail() {
    return this.userForm.get('userFormEmail');
  }

  get userFormPassword() {
    return this.userForm.get('userFormPassword');
  }

  get userFormConfirmPassword() {
    return this.userForm.get('userFormConfirmPassword');
  }

  get userFormCompanyName() {
    return this.userForm.get('userFormCompanyName');
  }

  get userFormBusinessUnit() {
    return this.userForm.get('userFormBusinessUnit');
  }

  get userFormCountry() {
    return this.userForm.get('userFormCountry');
  }

  createProfile() {
    this.registerUser();
  }

  getInputType(): string {
    return this.showPassword() ? 'text' : 'password';
  }

  selectTitle(event: Event): void {
    this.selectedTitle = (event.target as HTMLInputElement).value;
  }

  toggleShowPassword(): void {
    this.showPassword.update((showPassword) => !showPassword);
  }

  goHome() {
    void this.router.navigate(['auth', 'login']);
  }

  private initFormValidation(): FormGroup {
    return new FormGroup(
      {
        userFormTitle: new FormControl(''),
        userFormFirstName: new FormControl('', {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(64),
          ],
        }),
        userFormLastName: new FormControl('', {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(64),
          ],
        }),
        userFormCompanyName: new FormControl('', {
          validators: [Validators.required],
        }),
        userFormBusinessUnit: new FormControl('', {
          updateOn: 'blur',
        }),
        userFormCountry: new FormControl('', {
          validators: [Validators.required],
        }),
        userFormEmail: new FormControl('', {
          validators: [Validators.required, Validators.email],
        }),
        userFormPassword: new FormControl(''),
        userFormConfirmPassword: new FormControl(''),
        userFormConfirmPrivacyPolicy: new FormControl('', {
          validators: [Validators.requiredTrue],
        }),
      },
      {
        validators: [],
      },
    );
  }

  private registerUser(): Subscription {
    if (!this.enableDoubleOptIn()) {
      const generatedPassword = this.generateSecurePassword();
      this.userFormPassword?.setValue(generatedPassword);
      this.userFormConfirmPassword?.setValue(generatedPassword);
    }
    const registerUserRequestDto: RegisterUserRequestDto = {
      title: this.userFormTitle?.value || undefined,
      firstName: this.userFormFirstName?.value || undefined,
      lastName: this.userFormLastName?.value || undefined,
      companyName: this.userFormCompanyName?.value || undefined,
      businessUnit: this.userFormBusinessUnit?.value || undefined,
      country: this.userFormCountry?.value || undefined,
      email: this.userFormEmail?.value || undefined,
      password: this.userFormPassword?.value || undefined,
    };

    return this.coreAuthenticationService
      .register({
        xTenantSlug: this.authStore.tenantSlug(),
        registerUserRequestDto,
      })
      .subscribe({
        next: (response: Register201Response) => {
          // If the backend returns tokens (double opt-in disabled), have the user logged in
          if ('accessToken' in response && 'refreshToken' in response) {
            this.authStore.setAuthentication(response);
            void this.router.navigateByUrl(this.redirectUrl);
            return;
          }

          this.submitted.set(true);
        },
        error: (error) => {
          this.submitted.set(false);
          this.showAlert.set(true);
          this.logger.error(
            'Error while registering user: ',
            error.error?.message,
          );
          if (this.isEmailTakenError(error)) {
            this.genericError$$.next(false);
            this.emailTakenError$$.next(true);
            setTimeout(() => {
              this.showAlert.set(false);
            }, RegisterComponent.ALERT_DISPLAY_TIME);
          } else {
            this.emailTakenError$$.next(false);
            this.genericError$$.next(true);
          }
        },
      });
  }

  private getRegistrationConfiguration(): void {
    this.coreAuthenticationService
      .getRegistrationConfiguration({
        xTenantSlug: this.authStore.tenantSlug(),
      })
      .subscribe({
        next: (registrationConfiguration) => {
          this.enableDoubleOptIn.set(
            registrationConfiguration.enableDoubleOptIn,
          );
          this.updatePasswordFieldValidation();
          this.isLoading.set(false);
        },
        error: () => {
          this.enableDoubleOptIn.set(true);
          this.updatePasswordFieldValidation();
          this.isLoading.set(false);
        },
      });
  }

  private updatePasswordFieldValidation(): void {
    const passwordControl = this.userForm.get('userFormPassword');
    const confirmPasswordControl = this.userForm.get('userFormConfirmPassword');

    if (this.enableDoubleOptIn()) {
      passwordControl?.setValidators([
        Validators.required,
        Validators.pattern(
          /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{12,128}$/,
        ),
      ]);
      confirmPasswordControl?.setValidators([
        Validators.required,
        Validators.pattern(
          /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{12,128}$/,
        ),
      ]);
      this.userForm.setValidators([
        passwordsMatchValidator('userFormPassword', 'userFormConfirmPassword'),
      ]);
    } else {
      passwordControl?.clearValidators();
      confirmPasswordControl?.clearValidators();
      this.userForm.clearValidators();
    }

    passwordControl?.updateValueAndValidity();
    confirmPasswordControl?.updateValueAndValidity();
    this.userForm.updateValueAndValidity();
  }

  private generateSecurePassword(): string {
    // excludeSimilarCharacters: omit i/l/o/I/O/0/1
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const nums = '23456789';
    const syms = '!@#$%^&*';
    const all = lower + upper + nums + syms;

    const rand = (chars: string): string => {
      const arr = new Uint32Array(1);
      crypto.getRandomValues(arr);
      return chars[arr[0] % chars.length];
    };

    // strict: true — guarantee at least one char from each category
    const password = [
      rand(lower),
      rand(upper),
      rand(nums),
      rand(syms),
      ...Array.from({ length: 12 }, () => rand(all)),
    ];

    // Fisher-Yates shuffle with crypto randomness
    for (let i = password.length - 1; i > 0; i--) {
      const arr = new Uint32Array(1);
      crypto.getRandomValues(arr);
      const j = arr[0] % (i + 1);
      [password[i], password[j]] = [password[j], password[i]];
    }

    return password.join('');
  }

  private isEmailTakenError(error: HttpErrorResponse): boolean {
    //TODO Remove this hardcoded validation error check for existing email
    return (
      error.error?.message.includes('email') ||
      error.error?.message.includes('E-Mail')
    );
  }
}
